import {useCallback, useEffect, useMemo, useState} from 'react';
import {deletePushToken, getPushConfig, registerPushToken} from '../../../service/ChatService';
import {getChatDeviceId} from './chatDevice';

const SERVICE_WORKER_PATH = '/chat-push-sw.js';

function supported() {
    return typeof window !== 'undefined'
        && window.isSecureContext
        && 'Notification' in window
        && 'serviceWorker' in navigator
        && 'PushManager' in window;
}

function applicationServerKey(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(base64), character => character.charCodeAt(0));
}

async function serviceWorkerRegistration() {
    await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {scope: '/'});
    return navigator.serviceWorker.ready;
}

async function sendSubscription(subscription, signal) {
    await registerPushToken(getChatDeviceId(), {
        platform: 'WEB',
        push_transport: 'WEB_PUSH',
        web_push_subscription: subscription.toJSON(),
    }, signal);
}

export async function disableWebPushForLogout() {
    if (!supported()) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 1500);
    try {
        const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
        const subscription = await registration?.pushManager.getSubscription();
        await Promise.allSettled([
            subscription?.unsubscribe(),
            deletePushToken(getChatDeviceId(), controller.signal),
        ]);
    } finally {
        window.clearTimeout(timeout);
    }
}

export function useWebPushNotifications() {
    const [status, setStatus] = useState('checking');
    const [error, setError] = useState(null);

    const register = useCallback(async ({requestPermission = false, signal} = {}) => {
        if (!supported()) {
            setStatus('unsupported');
            return false;
        }
        setError(null);
        let permission = Notification.permission;
        if (permission === 'default' && !requestPermission) {
            setStatus('prompt');
            return false;
        }
        if (permission === 'default' && requestPermission) {
            permission = await Notification.requestPermission();
        }
        if (permission === 'denied') {
            setStatus('blocked');
            return false;
        }
        if (permission !== 'granted') {
            setStatus('prompt');
            return false;
        }
        const config = await getPushConfig(signal);
        if (!config?.push_enabled || !config?.web_push?.enabled || !config.web_push.public_key) {
            setStatus('unavailable');
            return false;
        }
        const registration = await serviceWorkerRegistration();
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription && !requestPermission) {
            setStatus('prompt');
            return false;
        }
        subscription ||= await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(config.web_push.public_key),
        });
        await sendSubscription(subscription, signal);
        setStatus('enabled');
        return true;
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        register({signal: controller.signal}).catch(registerError => {
            if (registerError?.name === 'AbortError') return;
            setError(registerError);
            setStatus('error');
        });
        return () => controller.abort();
    }, [register]);

    const enable = useCallback(async () => {
        setStatus('checking');
        try {
            return await register({requestPermission: true});
        } catch (enableError) {
            setError(enableError);
            setStatus('error');
            return false;
        }
    }, [register]);

    const disable = useCallback(async () => {
        setStatus('checking');
        setError(null);
        try {
            const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
            const subscription = await registration?.pushManager.getSubscription();
            await subscription?.unsubscribe();
            await deletePushToken(getChatDeviceId());
            setStatus('prompt');
            return true;
        } catch (disableError) {
            setError(disableError);
            setStatus('error');
            return false;
        }
    }, []);

    return useMemo(() => ({status, error, enable, disable}), [disable, enable, error, status]);
}
